import { useState } from "react";
import { motion } from "framer-motion";
import { Image, MessageSquare, Mic, FileText, Settings, Calendar, Users } from "lucide-react";
import type { IOSAppId } from "../types";

interface HomeScreenProps {
  onAppOpen: (appId: IOSAppId) => void;
  onShowControlCenter: () => void;
  onShowNotificationCenter: () => void;
}

// iOS 26 App Icons - More vibrant and refined
const apps: { id: IOSAppId; name: string; icon: React.ReactNode; gradient: string; notifications?: number }[] = [
  {
    id: "photos",
    name: "Photos",
    icon: <Image className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #ffffff 0%, #e8e8e8 50%, #f0f0f0 100%)",
    notifications: 0,
  },
  {
    id: "messages",
    name: "Messages",
    icon: <MessageSquare className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #30D158 0%, #28BD4B 100%)",
    notifications: 0,
  },
  {
    id: "voicememos",
    name: "Voice Memos",
    icon: <Mic className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #FF453A 0%, #E03A30 100%)",
    notifications: 0,
  },
  {
    id: "notes",
    name: "Notes",
    icon: <FileText className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #FFD60A 0%, #E6C109 100%)",
    notifications: 0,
  },
  {
    id: "contacts",
    name: "Contacts",
    icon: <Users className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #8E8E93 0%, #636366 100%)",
    notifications: 0,
  },
  {
    id: "calendar",
    name: "Calendar",
    icon: <Calendar className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #FF453A 0%, #E03A30 100%)",
    notifications: 0,
  },
  {
    id: "settings",
    name: "Settings",
    icon: <Settings className="h-7 w-7 text-white" strokeWidth={1.5} />,
    gradient: "linear-gradient(135deg, #8E8E93 0%, #636366 100%)",
    notifications: 0,
  },
];

const dockApps = ["photos", "messages", "voicememos", "notes"] as const;

export function HomeScreen({ onAppOpen, onShowControlCenter, onShowNotificationCenter }: HomeScreenProps) {
  const [pressedApp, setPressedApp] = useState<IOSAppId | null>(null);

  const handleAppPress = (appId: IOSAppId) => {
    setPressedApp(appId);
    setTimeout(() => {
      setPressedApp(null);
      onAppOpen(appId);
    }, 120);
  };

  const getAppById = (id: IOSAppId) => apps.find((app) => app.id === id)!;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative h-full w-full overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0c0c0e 0%, #1a1a1c 50%, #0f0f10 100%)",
      }}
    >
      {/* Subtle Noise Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Status Bar - iOS 26 Style */}
      <div className="flex items-center justify-between px-7 pt-[14px] text-white">
        <span className="text-[15px] font-semibold tracking-[-0.015em]">9:41</span>
        <div className="flex items-center gap-[5px]">
          {/* Signal Bars */}
          <div className="flex items-end gap-[2px] h-[12px]">
            <div className="w-[3px] h-[4px] bg-white rounded-[0.5px]" />
            <div className="w-[3px] h-[7px] bg-white rounded-[0.5px]" />
            <div className="w-[3px] h-[10px] bg-white rounded-[0.5px]" />
            <div className="w-[3px] h-[12px] bg-white rounded-[0.5px]" />
          </div>
          {/* WiFi */}
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none" className="ml-1">
            <path d="M8 1.5C5.5 1.5 3.2 2.5 1.5 4.2L2.9 5.6C4.2 4.3 6 3.5 8 3.5C10 3.5 11.8 4.3 13.1 5.6L14.5 4.2C12.8 2.5 10.5 1.5 8 1.5Z" fill="white"/>
            <path d="M8 5.5C6.5 5.5 5.1 6.1 4.1 7.1L5.5 8.5C6.2 7.8 7.1 7.4 8 7.4C8.9 7.4 9.8 7.8 10.5 8.5L11.9 7.1C10.9 6.1 9.5 5.5 8 5.5Z" fill="white"/>
            <circle cx="8" cy="10" r="1.5" fill="white"/>
          </svg>
          {/* Battery */}
          <div className="flex items-center ml-1">
            <div className="w-[25px] h-[12px] border border-white/30 rounded-[3px] p-[2px] relative">
              <div className="h-full w-[80%] bg-white rounded-[1px]" />
            </div>
            <div className="w-[1.5px] h-[6px] bg-white/30 rounded-r-[1px] ml-[1px]" />
          </div>
        </div>
      </div>

      {/* Notification/Control Center Gestures */}
      <div
        className="absolute left-0 top-0 h-14 w-full cursor-pointer z-10"
        onClick={onShowNotificationCenter}
      />
      <div
        className="absolute right-0 top-0 h-14 w-24 cursor-pointer z-10"
        onClick={onShowControlCenter}
      />

      {/* App Grid - iOS 26 Refined Spacing */}
      <div className="flex flex-col items-center justify-start px-6 pt-10">
        <div className="grid grid-cols-4 gap-x-5 gap-y-6">
          {apps.map((app, index) => (
            <motion.button
              key={app.id}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                delay: index * 0.04,
                type: "spring",
                stiffness: 300,
                damping: 20
              }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={() => handleAppPress(app.id)}
              className="flex flex-col items-center group"
            >
              {/* App Icon - iOS 26 Superellipse */}
              <motion.div
                animate={{
                  scale: pressedApp === app.id ? 0.92 : 1,
                }}
                transition={{ duration: 0.1 }}
                className="relative flex h-[60px] w-[60px] items-center justify-center overflow-hidden"
                style={{
                  borderRadius: "22.37%",
                  background: app.gradient,
                  boxShadow: `
                    0 8px 24px rgba(0, 0, 0, 0.22),
                    0 2px 4px rgba(0, 0, 0, 0.1),
                    inset 0 1px 0 rgba(255, 255, 255, 0.15),
                    inset 0 -1px 0 rgba(0, 0, 0, 0.05)
                  `,
                }}
              >
                {/* Gloss Effect */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)",
                    borderRadius: "inherit",
                  }}
                />
                
                {app.id === "photos" ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="absolute inset-0 rounded-[inherit] opacity-90"
                      style={{
                        background: "conic-gradient(from 180deg at 50% 50%, #FF3B30 0deg, #FF9500 51.43deg, #FFCC00 102.86deg, #34C759 154.29deg, #00C7BE 205.71deg, #007AFF 257.14deg, #5856D6 308.57deg, #FF3B30 360deg)",
                      }}
                    />
                    <Image className="relative z-10 h-7 w-7 text-white/90" strokeWidth={1.5} />
                  </div>
                ) : (
                  <div className="relative z-10 drop-shadow-sm">{app.icon}</div>
                )}

                {/* Notification Badge - iOS 26 Style */}
                {app.notifications && app.notifications > 0 && (
                  <div 
                    className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center px-1.5 text-[11px] font-bold text-white z-20"
                    style={{
                      background: "linear-gradient(135deg, #FF453A 0%, #E03A30 100%)",
                      borderRadius: "9px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {app.notifications > 99 ? "99+" : app.notifications}
                  </div>
                )}
              </motion.div>

              {/* App Name - iOS 26 Typography */}
              <span 
                className="mt-1.5 text-[11px] font-medium text-center"
                style={{
                  color: "rgba(255, 255, 255, 0.92)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                  letterSpacing: "-0.01em",
                }}
              >
                {app.name}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Dock - iOS 26 Glass Effect */}
      <div className="absolute bottom-5 left-4 right-4">
        <div 
          className="rounded-[32px] px-4 py-3"
          style={{
            background: "rgba(28, 28, 30, 0.72)",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.12),
              inset 0 1px 0 rgba(255, 255, 255, 0.05)
            `,
          }}
        >
          <div className="flex items-center justify-around">
            {dockApps.map((appId) => {
              const app = getAppById(appId);
              return (
                <motion.button
                  key={app.id}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => handleAppPress(app.id)}
                  className="flex flex-col items-center"
                >
                  <motion.div
                    animate={{
                      scale: pressedApp === app.id ? 0.92 : 1,
                    }}
                    transition={{ duration: 0.1 }}
                    className="relative flex h-[54px] w-[54px] items-center justify-center overflow-hidden"
                    style={{
                      borderRadius: "22.37%",
                      background: app.gradient,
                      boxShadow: `
                        0 8px 24px rgba(0, 0, 0, 0.22),
                        0 2px 4px rgba(0, 0, 0, 0.1),
                        inset 0 1px 0 rgba(255, 255, 255, 0.15)
                      `,
                    }}
                  >
                    {/* Gloss */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)",
                        borderRadius: "inherit",
                      }}
                    />
                    
                    {app.id === "photos" ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="absolute inset-0 rounded-[inherit] opacity-90"
                          style={{
                            background: "conic-gradient(from 180deg at 50% 50%, #FF3B30 0deg, #FF9500 51.43deg, #FFCC00 102.86deg, #34C759 154.29deg, #00C7BE 205.71deg, #007AFF 257.14deg, #5856D6 308.57deg, #FF3B30 360deg)",
                          }}
                        />
                        <Image className="relative z-10 h-6 w-6 text-white/90" strokeWidth={1.5} />
                      </div>
                    ) : (
                      <div className="relative z-10 scale-90">{app.icon}</div>
                    )}
                  </motion.div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Home Indicator - iOS 26 */}
      <div 
        className="absolute bottom-2 left-1/2 h-[5px] w-[134px] -translate-x-1/2 rounded-[2.5px]"
        style={{
          background: "rgba(255, 255, 255, 0.9)",
        }}
      />
    </motion.div>
  );
}